import React, { useEffect, useState } from 'react';
import { fetchData, postData } from '../components/misc';

export default function HomepageIntroVideoAdmin() {
  const [domains, setDomains] = useState({});
  const [settings, setSettings] = useState({});
  const [selectedDomain, setSelectedDomain] = useState('expatelitesingles.com');
  const [videoUrl, setVideoUrl] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    fetchData('/api/sirri_api/homepage_intro_video')
      .then((data) => {
        if (data?.responsecode === 200) {
          setDomains(data.domains || {});
          setSettings(data.settings || {});
          const firstDomain = Object.keys(data.domains || {})[0] || 'expatelitesingles.com';
          setSelectedDomain(firstDomain);
          const setting = (data.settings || {})[firstDomain] || {};
          const urls = setting.video_urls?.length ? setting.video_urls : (setting.video_url || setting.embed_url ? [setting.video_url || setting.embed_url] : []);
          setVideoUrl(urls.join('\n'));
          setEnabled(Boolean(setting.enabled));
        } else {
          setStatus(data?.message || 'Unable to load homepage intro video settings.');
        }
      })
      .catch((err) => {
        console.error('Failed to load homepage intro video settings', err);
        setStatus('Unable to load homepage intro video settings.');
      })
      .finally(() => setLoading(false));
  }, []);

  const chooseDomain = (domain) => {
    setSelectedDomain(domain);
    const setting = settings[domain] || {};
    const urls = setting.video_urls?.length ? setting.video_urls : (setting.video_url || setting.embed_url ? [setting.video_url || setting.embed_url] : []);
    setVideoUrl(urls.join('\n'));
    setEnabled(Boolean(setting.enabled));
    setStatus('');
  };

  const save = () => {
    setSaving(true);
    setStatus('Saving...');
    postData('/api/sirri_api/homepage_intro_video', {
      domain: selectedDomain,
      video_urls: videoUrl.split(/\n|,/).map((url) => url.trim()).filter(Boolean),
      enabled,
    })
      .then((data) => {
        if (data?.responsecode === 200) {
          setSettings((previous) => ({ ...previous, [selectedDomain]: data.setting }));
          const urls = data.setting?.video_urls?.length ? data.setting.video_urls : (data.setting?.video_url || data.setting?.embed_url ? [data.setting.video_url || data.setting.embed_url] : []);
          setVideoUrl(urls.join('\n'));
          setEnabled(Boolean(data.setting?.enabled));
          setStatus('Homepage intro video saved.');
        } else {
          setStatus(data?.message || 'Unable to save homepage intro video.');
        }
      })
      .catch((err) => {
        console.error('Failed to save homepage intro video', err);
        setStatus(err?.response?.data?.message || 'Unable to save homepage intro video.');
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return <p className="text-white">Loading homepage intro video settings...</p>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 rounded-lg bg-black/40 p-6 text-white">
      <div>
        <h1 className="text-2xl font-bold">Homepage Intro Video</h1>
        <p className="text-sm text-gray-300">
          Set the required first-visit testimonial videos for Penzi Halisi, Upendo Halisi, and Expat Elite Singles.
          Visitors must finish one before the page unlocks. Completion is remembered for 2 days, then the next unwatched testimonial is shown.
        </p>
      </div>

      <label className="block space-y-2">
        <span className="font-semibold">Domain</span>
        <select
          className="w-full rounded p-2 text-black"
          value={selectedDomain}
          onChange={(event) => chooseDomain(event.target.value)}
        >
          {Object.entries(domains).map(([domain, label]) => (
            <option key={domain} value={domain}>{label} — {domain}</option>
          ))}
        </select>
      </label>

      <label className="block space-y-2">
        <span className="font-semibold">Bunny/Mediadelivery testimonial video URLs</span>
        <textarea
          className="min-h-36 w-full rounded p-2 font-mono text-sm text-black"
          placeholder={"https://player.mediadelivery.net/play/746540/...\nhttps://player.mediadelivery.net/play/746540/..."}
          value={videoUrl}
          onChange={(event) => setVideoUrl(event.target.value)}
        />
        <span className="block text-xs text-gray-400">Paste one player.mediadelivery.net play or embed URL per line. Visitors will see an unwatched testimonial every 2 days until all have been watched.</span>
      </label>

      <label className="flex items-center gap-2">
        <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
        <span>Enable required intro testimonials for this domain</span>
      </label>

      <button
        type="button"
        className="rounded bg-pink-700 px-4 py-2 font-semibold text-white disabled:opacity-50"
        disabled={saving}
        onClick={save}
      >
        {saving ? 'Saving...' : 'Save intro testimonials'}
      </button>

      {status ? <p className="text-sm text-yellow-200">{status}</p> : null}

      <div className="rounded bg-black/30 p-3 text-xs text-gray-300">
        Current normalized iframe URLs:{' '}
        <span className="font-mono">{(settings[selectedDomain]?.embed_urls || []).join(', ') || settings[selectedDomain]?.embed_url || 'Not set'}</span>
      </div>
    </div>
  );
}
